export const API_CONSTANTS = {
  BASE_URL: "https://api.kinzy.club",
} as const;

// Asset URLs
export const ASSET_URLS = {
  BACK_BUTTON: "https://kinzy.club/games/assets/common/back_button.png",
  MUTE_BUTTON: "https://kinzy.club/games/assets/common/mute_button.png",
  UNMUTE_BUTTON: "https://kinzy.club/games/assets/common/unmute_button.png",
} as const;

// Game screens
export const GAME_SCREENS = {
  START: 'start',
  GAMEPLAY: 'gameplay',
  LEVEL_SELECTION: 'levelSelection',
  LEVEL_COMPLETE: 'levelComplete',
  MENU: 'menu',
  PLAYING: 'playing',
} as const;

export type GameScreen = typeof GAME_SCREENS[keyof typeof GAME_SCREENS];

// URL parameter names
export const URL_PARAMS = {
  SET: "set",
  LEVEL: "level",
  QUESTION: "question",
  TOKEN: "token",
} as const;

// Audio types
export const SOUND_TYPES = {
  CLICK: "click",
  SUCCESS: "success",
  ERROR: "error",
  LEVEL_COMPLETE: "levelComplete",
  GAME_START: "gameStart",
} as const;

export const MUSIC_TYPES = {
  EDUCATIONAL: "educational",
  ACTION: "action",
  PUZZLE: "puzzle",
  ADVENTURE: "adventure",
} as const;

// TTS configuration - Longer timeout for educational content with error boundaries
export const TTS_CONFIG = {
  AUTO_STOP_TIMEOUT: 30000, // ✅ 30 seconds for educational content (was 5000)
  AUDIO_VOLUME: 0.8,
} as const;

// Loading timeouts
export const LOADING_TIMEOUTS = {
  DEFAULT_TIMEOUT: 10000, // 10 seconds default loading timeout
} as const;

// Game states
export const GAME_STATES = {
  MENU: 'menu',
  PLAYING: 'playing',
  PAUSED: 'paused',
  COMPLETE: 'complete'
} as const;

export type GameState = typeof GAME_STATES[keyof typeof GAME_STATES];

// UI Text
export const UI_TEXT = {
  LOADING: 'Loading...',
  CHOOSE_LEVEL: 'Choose Your Kingdom',
  PROGRESS: 'Progress',
  LEVELS: 'Levels',
  COMPLETE: 'Complete',
  COMPLETED: 'Completed',
  UNLOCKED: 'Unlocked',
  STARS: 'Stars',
  SCORE: 'Score'
} as const;

// Button Text
export const BUTTON_TEXT = {
  START_GAME: '🏰 Enter the Kingdom',
  HOW_TO_PLAY: '📖 How to Play',
  CONTINUE: 'Continue',
  NEXT_LEVEL: 'Next Kingdom',
  RETRY_LEVEL: 'Try Again',
  BACK_TO_MENU: 'Back to Kingdoms',
  GO_BACK: 'Go back',
} as const;

// Score Text
export const SCORE_TEXT = {
  POINTS: 'points',
  QUESTION: 'Battle',
  OF: 'of'
} as const;

// Feedback Messages
export const FEEDBACK_MESSAGES = {
  CORRECT: 'Victory! 🎉',
  NOT_QUITE: 'Almost there! 😅',
  CORRECT_ANSWER: 'Winning Move:',
  EXPLANATION: 'Strategy:',
  CONTINUE: 'Continue Adventure'
} as const;

// Hint Text
export const HINT_TEXT = {
  SHOW_HINT: 'Show Hint',
  HIDE_HINT: 'Hide Hint'
} as const;

// Animations
export const ANIMATIONS = {
  FADE_DURATION: 600,
  SLIDE_DURATION: 400,
  BOUNCE_DURATION: 800,
  HOVER_SCALE: 1.05,
  ACTIVE_SCALE: 0.95,
  BOUNCE_DELAY: 500,
  HINT_FADE_DURATION: 300,
  BACK_BUTTON_PRESS_DURATION: 150,
} as const;

// Z-index constants for proper layering
export const Z_INDEX = {
  BACK_BUTTON: 50,
  MODAL: 50,
  OVERLAY: 40,
  HINT_BUTTON: 50,
  HINT_BUBBLE: 40,
  DROPDOWN: 30,
  HEADER: 20,
  CONTENT: 10,
} as const;

// Audio mute constants
export const AUDIO_MUTE = {
  STORAGE_PREFIX: "kinzy_audio_muted_",
  DEFAULT_MUTED: false,
  BUTTON_SIZE: "w-12 h-12",
  BUTTON_POSITION: "fixed top-4 right-4 z-40",
  BUTTON_GRADIENT: "bg-gradient-to-br from-blue-500 to-purple-600",
  BUTTON_HOVER: "hover:from-blue-600 hover:to-purple-700",
  ICON_SIZE: "w-5 h-5",
  LOG_PREFIX: "[AUDIO_MUTE]",
} as const;

// Types
export type URLParam = (typeof URL_PARAMS)[keyof typeof URL_PARAMS];
export type SoundType = (typeof SOUND_TYPES)[keyof typeof SOUND_TYPES];
export type MusicType = (typeof MUSIC_TYPES)[keyof typeof MUSIC_TYPES];
export type TTSConfig = (typeof TTS_CONFIG)[keyof typeof TTS_CONFIG];
export type LoadingTimeout = (typeof LOADING_TIMEOUTS)[keyof typeof LOADING_TIMEOUTS];
export type AudioMute = (typeof AUDIO_MUTE)[keyof typeof AUDIO_MUTE];